
"use client"

import { useState, useCallback, useRef, useMemo, useEffect } from 'react'
import type { GenerationConfig } from '@/types/autoGeneration'

interface QueueItem {
  id: string
  fn: (abortSignal?: AbortSignal) => Promise<any>
  options: {
    priority: number
    maxRetries: number
    type: string
  }
  retries: number
  controller?: AbortController
}

interface QueueMetrics {
  totalCalls: number
  successfulCalls: number
  failedCalls: number
  averageResponseTime: number
  lastCallTime?: Date
}

interface IntervalConfig {
  startMinute: number
  endMinute: number
  maxCallsPerInterval: number
  callIntervalSeconds: number
  enabled: boolean
  description?: string
}

const useLLMQueue = (timeBasedConfig: GenerationConfig['timeBasedConfig'], recordingStartTime: Date | null) => {
  const [queue, setQueue] = useState<QueueItem[]>([])
  const [isProcessing, setIsProcessing] = useState(false)
  const [metrics, setMetrics] = useState<QueueMetrics>({
    totalCalls: 0,
    successfulCalls: 0,
    failedCalls: 0,
    averageResponseTime: 0
  })

  // Track calls per interval and type
  const callTracking = useRef<{
    [intervalKey: string]: {
      totalCalls: number
      typeCalls: { [type: string]: number }
      lastCallTime: Date | null
      typeLastCallTime: { [type: string]: Date | null }
    }
  }>({})

  // Add reference to currently processing item for cancellation
  const currentProcessingItem = useRef<QueueItem | null>(null)

  const getCurrentTimeConfig = useCallback((): IntervalConfig | null => {
    if (!recordingStartTime) return null
    
    const elapsedMinutes = (Date.now() - recordingStartTime.getTime()) / (1000 * 60)
    
    return timeBasedConfig.intervals.find(interval => 
      elapsedMinutes >= interval.startMinute && 
      elapsedMinutes < interval.endMinute &&
      interval.enabled
    ) || null
  }, [timeBasedConfig.intervals, recordingStartTime])

  const getNextAvailableInterval = useCallback((): IntervalConfig | null => {
    if (!recordingStartTime) return null
    
    const elapsedMinutes = (Date.now() - recordingStartTime.getTime()) / (1000 * 60)
    
    return timeBasedConfig.intervals.find(interval => 
      elapsedMinutes < interval.startMinute && interval.enabled
    ) || null
  }, [timeBasedConfig.intervals, recordingStartTime])

  const canMakeCall = useCallback((type: string): boolean => {
    const currentConfig = getCurrentTimeConfig()
    if (!currentConfig) return false

    const intervalKey = `${currentConfig.startMinute}-${currentConfig.endMinute}`
    const tracking = callTracking.current[intervalKey]
    
    if (!tracking) {
      console.log(`✅ [${type.toUpperCase()}] Call allowed - No tracking data yet`)
      return true
    }

    // Check if enough time has passed since last call of this specific type
    if (tracking.typeLastCallTime && tracking.typeLastCallTime[type]) {
      const timeSinceLastTypeCall = (Date.now() - tracking.typeLastCallTime[type]!.getTime()) / 1000
      if (timeSinceLastTypeCall < currentConfig.callIntervalSeconds) {
        console.log(`🚫 [${type.toUpperCase()}] Call blocked - Not enough time since last ${type} call (${timeSinceLastTypeCall.toFixed(1)}s < ${currentConfig.callIntervalSeconds}s)`)
        return false
      }
    }

    // Check type-specific limits (cada tipo tiene su propio límite independiente)
    const typeCalls = tracking.typeCalls[type] || 0
    if (typeCalls >= currentConfig.maxCallsPerInterval) {
      console.log(`🚫 [${type.toUpperCase()}] Call blocked - Type limit reached (${typeCalls}/${currentConfig.maxCallsPerInterval})`)
      return false
    }

    // REMOVIDO: Ya no verificamos el límite total combinado
    // Cada tipo puede usar hasta maxCallsPerInterval independientemente

    console.log(`✅ [${type.toUpperCase()}] Call allowed - Type calls: ${typeCalls}/${currentConfig.maxCallsPerInterval}`)
    return true
  }, [getCurrentTimeConfig])

  // Add cancellation function
  const cancelCurrentCall = useCallback(() => {
    if (currentProcessingItem.current?.controller) {
      console.log('🚫 [LLM_QUEUE] Cancelling current API call due to editing')
      currentProcessingItem.current.controller.abort()
      currentProcessingItem.current = null
    }
    
    // Clear the queue of pending calls
    setQueue([])
    console.log('🚫 [LLM_QUEUE] Cleared pending queue due to editing')
  }, [])

  const addToQueue = useCallback(async (
    fn: (abortSignal?: AbortSignal) => Promise<any>, 
    options: { priority: number; maxRetries: number; type: string }
  ): Promise<any> => {
    return new Promise((resolve, reject) => {
      const id = Math.random().toString(36).substr(2, 9)
      const controller = new AbortController()
      
      const queueItem: QueueItem = {
        id,
        fn: async () => {
          try {
            currentProcessingItem.current = queueItem
            const result = await fn(controller.signal)
            currentProcessingItem.current = null
            resolve(result)
            return result
          } catch (error) {
            currentProcessingItem.current = null
            if (error.name === 'AbortError' || error.message.includes('cancelled')) {
              console.log(`🚫 [LLM_QUEUE] Call ${options.type} was cancelled`)
              reject(new Error('Call cancelled due to editing'))
            } else {
              reject(error)
            }
            throw error
          }
        },
        options,
        retries: 0,
        controller
      }

      setQueue(prev => {
        const newQueue = [...prev, queueItem].sort((a, b) => b.options.priority - a.options.priority)
        console.log(`📝 [QUEUE] Added ${options.type} call to queue`)
        return newQueue
      })
    })
  }, [])

  const processQueue = useCallback(async () => {
    if (isProcessing || queue.length === 0) return

    setIsProcessing(true)
    const item = queue[0]
    
    try {
      console.log(`🚀 [LLM_QUEUE] Processing ${item.options.type} call in interval ${getCurrentTimeConfig()?.description || 'unknown'}`)
      
      const startTime = Date.now()
      await item.fn()
      const endTime = Date.now()
      
      // Update metrics
      setMetrics(prev => {
        const newTotal = prev.totalCalls + 1
        const newAvg = ((prev.averageResponseTime * prev.successfulCalls) + (endTime - startTime)) / (prev.successfulCalls + 1)
        
        return {
          ...prev,
          totalCalls: newTotal,
          successfulCalls: prev.successfulCalls + 1,
          averageResponseTime: newAvg,
          lastCallTime: new Date()
        }
      })

      // Update call tracking
      const currentConfig = getCurrentTimeConfig()
      if (currentConfig) {
        const intervalKey = `${currentConfig.startMinute}-${currentConfig.endMinute}`
        
        if (!callTracking.current[intervalKey]) {
          console.log(`🔄 [QUEUE] Switching to new interval: ${currentConfig.description}`)
          callTracking.current[intervalKey] = {
            totalCalls: 0,
            typeCalls: {},
            lastCallTime: null,
            typeLastCallTime: {}
          }
        }
        
        const tracking = callTracking.current[intervalKey]
        tracking.totalCalls += 1
        tracking.typeCalls[item.options.type] = (tracking.typeCalls[item.options.type] || 0) + 1
        tracking.lastCallTime = new Date()
        tracking.typeLastCallTime[item.options.type] = new Date()
        
        console.log(`📝 [QUEUE] ${item.options.type} call completed. ${item.options.type} calls in interval: ${tracking.typeCalls[item.options.type]}`)
      }

      console.log(`✅ [LLM_QUEUE] ${item.options.type} call completed`)
      
    } catch (error) {
      if (error.message !== 'Call cancelled due to editing') {
        console.error(`❌ [LLM_QUEUE] ${item.options.type} call failed:`, error)
        
        setMetrics(prev => ({
          ...prev,
          totalCalls: prev.totalCalls + 1,
          failedCalls: prev.failedCalls + 1
        }))

        // Retry logic
        if (item.retries < item.options.maxRetries) {
          item.retries += 1
          setQueue(prev => [...prev.slice(1), item])
          console.log(`🔄 [LLM_QUEUE] Retrying ${item.options.type} call (${item.retries}/${item.options.maxRetries})`)
        }
      }
    } finally {
      setQueue(prev => prev.slice(1))
      setIsProcessing(false)
    }
  }, [isProcessing, queue, getCurrentTimeConfig])

  useEffect(() => {
    if (!isProcessing && queue.length > 0) {
      processQueue()
    }
  }, [queue, isProcessing, processQueue])

  const clearQueue = useCallback(() => {
    cancelCurrentCall()
    setQueue([])
    console.log('🧹 [LLM_QUEUE] Queue cleared')
  }, [cancelCurrentCall])

  const getQueueStatus = useCallback(() => {
    const currentConfig = getCurrentTimeConfig()
    const intervalKey = currentConfig ? `${currentConfig.startMinute}-${currentConfig.endMinute}` : null
    const tracking = intervalKey ? callTracking.current[intervalKey] : null
    
    return {
      queueLength: queue.length,
      isProcessing,
      currentInterval: currentConfig,
      callsInCurrentInterval: tracking?.totalCalls || 0,
      maxCallsInInterval: currentConfig?.maxCallsPerInterval || 0,
      canMakeMoreCalls: currentConfig ? (tracking?.totalCalls || 0) < currentConfig.maxCallsPerInterval : false
    }
  }, [queue.length, isProcessing, getCurrentTimeConfig])

  return {
    addToQueue,
    clearQueue,
    cancelCurrentCall,
    getQueueStatus,
    getCurrentTimeConfig,
    getNextAvailableInterval,
    canMakeCall,
    metrics,
  }
}

export default useLLMQueue
